import React, { useMemo, useState, useEffect } from 'react';
import CobuText from './CobuText';

export interface CobuDateTimePickerProps {
  label?: string;
  value: Date | string | null;
  onChange: (date: Date) => void;
  error?: string;
  placeholder?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  mode?: 'date' | 'time' | 'datetime';
  selectDateLabel?: string;
  selectTimeLabel?: string;
  selectDateTimeLabel?: string;
  chooseDateLabel?: string;
  chooseTimeLabel?: string;
  cancelLabel?: string;
  confirmLabel?: string;
}

const CobuDateTimePicker: React.FC<CobuDateTimePickerProps> = (props) => {
  const valueDate: Date | null = useMemo(() => {
    if (!props.value) return null;
    return typeof props.value === 'string' ? new Date(props.value) : props.value;
  }, [props.value]);

  // Format for HTML input value (must be ISO format: YYYY-MM-DD)
  const formatDateForInput = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  // Format for HTML input value (must be HH:mm format)
  const formatTimeForInput = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Format for display text (localized)
  const formatDateForDisplay = (d: Date) => {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTimeForDisplay = (d: Date) => {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const [open, setOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date | null>(() =>
    valueDate ? new Date(valueDate) : null
  );
  const [hasTime, setHasTime] = useState<boolean>(() => !!valueDate);

  // Sync tempDate with valueDate when modal opens (only on open, not on valueDate changes)
  useEffect(() => {
    if (open) {
      setTempDate(valueDate ? new Date(valueDate) : null);
      setHasTime(!!valueDate);
    }
  }, [open]); // intentionally not depending on valueDate to avoid mid-open resets

  const defaultPlaceholder =
    props.mode === 'date'
      ? props.selectDateLabel ?? 'Select date'
      : props.mode === 'time'
      ? props.selectTimeLabel ?? 'Select time'
      : props.selectDateTimeLabel ?? 'Select date & time';

  const display = valueDate
    ? `${formatDateForDisplay(valueDate)} ${formatTimeForDisplay(valueDate)}`
    : props.placeholder || defaultPlaceholder;

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
        {props.label ? (
          <CobuText variant="label" color={'$color' as any}>
            {props.label}
          </CobuText>
        ) : null}
        <div
          onClick={() => setOpen(true)}
          style={{
            width: '100%',
            padding: 12,
            minHeight: 48,
            borderRadius: 12,
            border: '1px solid var(--borderColor)',
            background: 'var(--whiteTransparent70)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          aria-label={props.label}
        >
          <CobuText
            variant="body"
            color={(valueDate ? '$color' : '$colorMuted') as any}
          >
            {display}
          </CobuText>
          {/* Always show chevron in custom mode; reads as dropdown */}
          <span aria-hidden style={{ color: 'var(--colorMuted)', marginLeft: 8 }}>
            ▾
          </span>
        </div>
        {props.error ? (
          <CobuText variant="caption" color={'$error' as any}>
            {props.error}
          </CobuText>
        ) : null}
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--blackTransparent50)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{
              background: 'var(--background)',
              borderRadius: 12,
              padding: 16,
              width: '90%',
              maxWidth: 420,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', gap: 8, width: '100%', marginBottom: 12 }}>
              {/* Date field – the native <input type="date"> is the whole card */}
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  minHeight: 48,
                  borderRadius: 12,
                  border: '1px solid var(--borderColor)',
                  background: 'var(--whiteTransparent70)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {!tempDate && (
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 12px',
                      color: 'var(--colorMuted)',
                      fontSize: 16,
                      pointerEvents: 'none',
                    }}
                  >
                    <span>{props.chooseDateLabel ?? 'Choose date'}</span>
                    <span style={{ marginLeft: 8 }}>📅</span>
                  </div>
                )}
                <input
                  type="date"
                  value={tempDate ? formatDateForInput(tempDate) : ''}
                  min={
                    props.minimumDate ? formatDateForInput(props.minimumDate) : undefined
                  }
                  max={
                    props.maximumDate ? formatDateForInput(props.maximumDate) : undefined
                  }
                  onChange={(e) => {
                    const dateValue = e.target.value;
                    if (dateValue) {
                      const [year, month, day] = dateValue.split('-').map(Number);
                      setTempDate((prevDate) => {
                        const hours = prevDate ? prevDate.getHours() : 0;
                        const minutes = prevDate ? prevDate.getMinutes() : 0;
                        return new Date(
                          year,
                          month - 1,
                          day,
                          hours,
                          minutes,
                          0,
                          0
                        );
                      });
                    } else {
                      setTempDate(null);
                      setHasTime(false);
                    }
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 12px',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: 16,
                    color: tempDate ? 'var(--color)' : 'transparent',
                    caretColor: tempDate ? undefined : 'transparent',
                  } as React.CSSProperties}
                />
              </div>

              {/* Time field – the native <input type="time"> is the whole card */}
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  minHeight: 48,
                  borderRadius: 12,
                  border: '1px solid var(--borderColor)',
                  background: 'var(--whiteTransparent70)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {!(hasTime && tempDate) && (
                  <div
                    aria-hidden
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0 12px',
                      color: 'var(--colorMuted)',
                      fontSize: 16,
                      pointerEvents: 'none',
                    }}
                  >
                    <span>{props.chooseTimeLabel ?? 'Choose time'}</span>
                    <span style={{ marginLeft: 8 }}>🕒</span>
                  </div>
                )}
                <input
                  type="time"
                  value={tempDate ? formatTimeForInput(tempDate) : ''}
                  onChange={(e) => {
                    const timeValue = e.target.value || '00:00';
                    const [hours, minutes] = timeValue.split(':').map(Number);
                    setTempDate((prevDate) => {
                      const base = prevDate || new Date();
                      const year = base.getFullYear();
                      const month = base.getMonth();
                      const day = base.getDate();
                      return new Date(
                        year,
                        month,
                        day,
                        hours,
                        minutes,
                        0,
                        0
                      );
                    });
                    setHasTime(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '12px 12px',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    fontSize: 16,
                    color: hasTime && tempDate ? 'var(--color)' : 'transparent',
                    caretColor: hasTime && tempDate ? undefined : 'transparent',
                  } as React.CSSProperties}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setOpen(false)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid var(--borderColor)',
                  background: 'transparent',
                }}
              >
                {props.cancelLabel ?? 'Cancel'}
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  if (tempDate) {
                    props.onChange(tempDate);
                  }
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: '$cobuDarkBlue',
                  color: '$white' as any,
                }}
              >
                {props.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CobuDateTimePicker;